import { api, API_BASE } from "@/lib/apiClient";

export type GeschaeftspartnerTyp = "kunde" | "lieferant";
export type Partnerart = "firma" | "privatperson";
export type Rechnungsformat = "zugpferd" | "xrechnung";
export type Zahlungsart = "rechnung" | "vorkasse";

export interface KundeMailKanal {
  email: string | null;
  cc_projektansprechpartner: boolean;
}

export interface KundeRechnungsEinstellungen {
  rechnungsformat: Rechnungsformat;
  leitweg_id: string | null;
  /** USt-IdNr. des Kunden (E-Rechnung BT-48, Pflicht bei § 13b Reverse Charge). */
  ust_id: string | null;
  /** § 13b UStG (Reverse Charge) als Standard für neue Rechnungen dieses Kunden. */
  reverse_charge_13b: boolean;
  /** Bei ZUGFeRD: Stundennachweis-PDF beim Rechnungsversand standardmäßig anhängen. */
  stundennachweis_mit_rechnung: boolean;
  rechnung: KundeMailKanal;
  zahlungserinnerung: KundeMailKanal;
  mahnung_1: KundeMailKanal;
  mahnung_2: KundeMailKanal;
  mahnung_3: KundeMailKanal;
}

export const DEFAULT_KUNDE_RECHNUNGS_EINSTELLUNGEN: KundeRechnungsEinstellungen = {
  rechnungsformat: "zugpferd",
  leitweg_id: null,
  ust_id: null,
  reverse_charge_13b: false,
  stundennachweis_mit_rechnung: true,
  rechnung: { email: null, cc_projektansprechpartner: false },
  zahlungserinnerung: { email: null, cc_projektansprechpartner: false },
  mahnung_1: { email: null, cc_projektansprechpartner: false },
  mahnung_2: { email: null, cc_projektansprechpartner: false },
  mahnung_3: { email: null, cc_projektansprechpartner: false },
};

export interface ZahlungsbedingungenStandard {
  zahlungsart: Zahlungsart;
  zahlungsziel_tage: number;
  skonto_prozent: number;
  skontoziel_tage: number;
}

export interface ZahlungsbedingungenOverride {
  zahlungsart: Zahlungsart | null;
  zahlungsziel_tage: number | null;
  skonto_prozent: number | null;
  skontoziel_tage: number | null;
}

export interface ProjektZahlungsbedingungen {
  auftrag_id: number;
  auftragsnummer: string | null;
  beschreibung: string | null;
  override: ZahlungsbedingungenOverride;
  effektiv: ZahlungsbedingungenStandard;
}

export const DEFAULT_ZAHLUNGSBEDINGUNGEN_STANDARD: ZahlungsbedingungenStandard = {
  zahlungsart: "rechnung",
  zahlungsziel_tage: 14,
  skonto_prozent: 0,
  skontoziel_tage: 0,
};

export const EMPTY_ZAHLUNGSBEDINGUNGEN_OVERRIDE: ZahlungsbedingungenOverride = {
  zahlungsart: null,
  zahlungsziel_tage: null,
  skonto_prozent: null,
  skontoziel_tage: null,
};

export const ZAHLUNGSART_OPTIONS: { value: Zahlungsart; label: string }[] = [
  { value: "rechnung", label: "Rechnung" },
  { value: "vorkasse", label: "Vorkasse" },
];

export interface Geschaeftspartner {
  id: number;
  hero_contact_id: number | null;
  typ: GeschaeftspartnerTyp;
  firmenname: string;
  partnerart?: Partnerart | null;
  anrede?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  email: string | null;
  telefon: string | null;
  adresszeile1?: string | null;
  adresszeile2?: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  is_deleted: boolean;
  hero_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  ansprechpartner_count: number | null;
  /** Nur bei Suche: Namen der Ansprechpartner, die zum Suchbegriff passen. */
  matched_ansprechpartner?: string | null;
  /** Nur bei Suche: IDs der passenden Ansprechpartner, zum Hervorheben in der Detailansicht. */
  matched_ansprechpartner_ids?: number[] | null;
  rechnungs_einstellungen: KundeRechnungsEinstellungen | null;
  zahlungsbedingungen: ZahlungsbedingungenOverride | null;
  zahlungsbedingungen_effektiv: ZahlungsbedingungenStandard | null;
  kundencode: number | null;
  kundennummer: string | null;
  lieferantennummer: string | null;
  notizen?: string | null;
  artikel_count: number | null;
  /** Nur in Listen: Anzahl Projekte (Kunde). */
  projekte_count?: number | null;
  /** Nur in Listen: Anzahl Bestellungen (Lieferant). */
  bestellungen_count?: number | null;
  /** Nur in Listen: Anzahl erkannter Eingangsrechnungen (Lieferant). */
  eingangsrechnungen_count?: number | null;
  /** Einzelansicht: Löschen ist erlaubt, solange keine Projekte bzw. Bestellungen hängen. */
  kann_geloescht_werden?: boolean | null;
  /** Einzelansicht: Grund, warum Löschen gerade nicht möglich ist. */
  loesch_hinweis?: string | null;
  has_logo?: boolean | null;
  logo_updated_at?: string | null;
}

/** Detail-URL. Bei einem Suchtreffer über Ansprechpartner landet die Seite direkt bei diesen Personen. */
export function geschaeftspartnerDetailPath(
  detailBasePath: string,
  row: Pick<Geschaeftspartner, "id" | "matched_ansprechpartner_ids">,
): string {
  const ids = (row.matched_ansprechpartner_ids ?? []).filter((id) => Number.isFinite(id) && id > 0);
  const base = `${detailBasePath}/${row.id}`;
  if (!ids.length) return base;
  const params = new URLSearchParams();
  params.set("tab", "kontakte");
  params.set("ansprechpartner", ids.join(","));
  return `${base}?${params.toString()}`;
}

export interface GeschaeftspartnerIn {
  firmenname: string;
  partnerart?: Partnerart;
  anrede?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  email?: string | null;
  telefon?: string | null;
  adresszeile1?: string | null;
  adresszeile2?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  kundencode?: number | null;
  kundennummer?: string | null;
  lieferantennummer?: string | null;
  notizen?: string | null;
}

export const ANSPRECHPARTNER_ANREDEN = ["Herr", "Frau", "Divers"] as const;

export type AnsprechpartnerAnrede = (typeof ANSPRECHPARTNER_ANREDEN)[number] | "";

export function ansprechpartnerAnredeOptions(current?: string | null): string[] {
  const extra = (current || "").trim();
  if (extra && !(ANSPRECHPARTNER_ANREDEN as readonly string[]).includes(extra)) {
    return [...ANSPRECHPARTNER_ANREDEN, extra];
  }
  return [...ANSPRECHPARTNER_ANREDEN];
}

export interface KontaktAnsprechpartner {
  id: number;
  hero_contact_id: number | null;
  vorname: string | null;
  nachname: string | null;
  anrede: string | null;
  telefonnummer: string | null;
  mobiltelefon?: string | null;
  funktion?: string | null;
  mail: string | null;
  team_mail?: string | null;
  team_mail_dokumentarten?: string[];
  geschaeftspartner_id: number | null;
  firmennamen: string | null;
  deleted: boolean | null;
}

export interface KontaktAnsprechpartnerIn {
  vorname: string;
  nachname: string;
  anrede: string;
  telefonnummer: string;
  mobiltelefon?: string;
  funktion?: string;
  mail: string;
  team_mail?: string;
  team_mail_dokumentarten?: string[];
  geschaeftspartner_id: number;
}

/** Dokumentarten, die an einen Ansprechpartner gehen und optional auch an die Team-Mail. */
export const ANSPRECHPARTNER_TEAM_MAIL_DOKUMENTARTEN: ReadonlyArray<{
  key: string;
  label: string;
}> = [
  { key: "angebot", label: "Angebot" },
  { key: "angebot_nachfass", label: "Angebots-Nachfass" },
  { key: "auftragsbestaetigung", label: "Auftragsbestätigung" },
  { key: "lieferschein", label: "Lieferschein" },
  { key: "rechnung", label: "Rechnung" },
  { key: "teilrechnung", label: "Teilrechnung" },
  { key: "schlussrechnung", label: "Schlussrechnung" },
  { key: "vorkasse", label: "Vorkasse-Rechnung" },
  { key: "stornorechnung", label: "Stornorechnung" },
  { key: "stundennachweis", label: "Stundennachweis" },
  { key: "stundennachweis_erinnerung", label: "Stundennachweis-Erinnerung" },
  { key: "zahlungserinnerung", label: "Zahlungserinnerung" },
  { key: "mahnung_1", label: "1. Mahnung" },
  { key: "mahnung_2", label: "2. Mahnung" },
  { key: "mahnung_3", label: "3. Mahnung" },
];

export interface GeschaeftspartnerListResponse {
  items: Geschaeftspartner[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListGeschaeftspartnerParams {
  q?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  direction?: "asc" | "desc";
  kundencode?: number;
  hero_contact_id?: number;
}

export interface SyncResult {
  kunden: number;
  lieferanten: number;
  ansprechpartner: number;
  /** Wie viele Lieferanten aus Hero eine Lieferantennummer mitgebracht haben. */
  lieferanten_mit_nummer?: number;
}

export function listKontaktKunden(params?: ListGeschaeftspartnerParams): Promise<GeschaeftspartnerListResponse> {
  const search = new URLSearchParams();
  if (params?.q) search.set("q", params.q);
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  if (params?.sort) search.set("sort", params.sort);
  if (params?.direction) search.set("direction", params.direction);
  if (params?.kundencode != null) search.set("kundencode", String(params.kundencode));
  if (params?.hero_contact_id != null) {
    search.set("hero_contact_id", String(params.hero_contact_id));
  }
  const qs = search.toString();
  return api.get<GeschaeftspartnerListResponse>(`/kontakte/kunden${qs ? `?${qs}` : ""}`);
}

export function listKontaktLieferanten(params?: ListGeschaeftspartnerParams): Promise<GeschaeftspartnerListResponse> {
  const search = new URLSearchParams();
  if (params?.q) search.set("q", params.q);
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  if (params?.sort) search.set("sort", params.sort);
  if (params?.direction) search.set("direction", params.direction);
  const qs = search.toString();
  return api.get<GeschaeftspartnerListResponse>(`/kontakte/lieferanten${qs ? `?${qs}` : ""}`);
}

export function getGeschaeftspartner(id: number): Promise<Geschaeftspartner> {
  return api.get<Geschaeftspartner>(`/kontakte/geschaeftspartner/${id}`);
}

export function uploadGeschaeftspartnerLogo(id: number, file: File): Promise<Geschaeftspartner> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm<Geschaeftspartner>(`/kontakte/geschaeftspartner/${id}/logo`, form);
}

export function deleteGeschaeftspartnerLogo(id: number): Promise<Geschaeftspartner> {
  return api.del<Geschaeftspartner>(`/kontakte/geschaeftspartner/${id}/logo`);
}

export function listGeschaeftspartnerAnsprechpartner(
  geschaeftspartnerId: number,
): Promise<KontaktAnsprechpartner[]> {
  return api.get<KontaktAnsprechpartner[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/ansprechpartner`,
    { cache: "no-store" },
  );
}

export interface Objektadresse {
  id?: number | null;
  address_id: number;
  title: string;
  street?: string | null;
  city?: string | null;
  zipcode?: string | null;
  maps_link?: string | null;
  ort_id?: number | null;
  deleted?: boolean;
  editable?: boolean;
}

export interface ObjektadresseIn {
  title: string;
  street?: string | null;
  city?: string | null;
  zipcode?: string | null;
  address_id?: number | null;
}

export function listGeschaeftspartnerObjektadressen(
  geschaeftspartnerId: number,
): Promise<Objektadresse[]> {
  return api.get<Objektadresse[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/objektadressen`,
  );
}

export function createGeschaeftspartnerObjektadresse(
  geschaeftspartnerId: number,
  body: ObjektadresseIn,
): Promise<Objektadresse> {
  return api.post<Objektadresse>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/objektadressen`,
    body,
  );
}

export function updateGeschaeftspartnerObjektadresse(
  geschaeftspartnerId: number,
  ortId: number,
  body: ObjektadresseIn,
): Promise<Objektadresse> {
  return api.put<Objektadresse>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/objektadressen/${ortId}`,
    body,
  );
}

export function deleteGeschaeftspartnerObjektadresse(
  geschaeftspartnerId: number,
  ortId: number,
): Promise<{ message: string }> {
  return api.del<{ message: string }>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/objektadressen/${ortId}`,
  );
}

export function deleteGeschaeftspartnerHeroObjektadresse(
  geschaeftspartnerId: number,
  addressId: number,
): Promise<{ message: string }> {
  return api.del<{ message: string }>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/objektadressen/hero/${addressId}`,
  );
}

export function formatObjektadresseLine(addr: {
  title?: string | null;
  street?: string | null;
  city?: string | null;
  zipcode?: string | null;
}): string {
  const plzOrt = [addr.zipcode, addr.city].filter(Boolean).join(" ");
  const line = [addr.street, plzOrt].filter(Boolean).join(", ");
  return line || addr.title?.trim() || "—";
}

export function syncGeschaeftspartnerAnsprechpartner(
  geschaeftspartnerId: number,
): Promise<KontaktAnsprechpartner[]> {
  return api.post<KontaktAnsprechpartner[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/sync-ansprechpartner`,
    undefined,
    { timeoutMs: 0 },
  );
}

export function syncKontakteFromHero(): Promise<SyncResult> {
  // Hero-Sync kann bei vielen Kontakten mehrere Minuten dauern.
  return api.post<SyncResult>("/kontakte/sync", undefined, { timeoutMs: 0 });
}

export function createGeschaeftspartner(
  typ: GeschaeftspartnerTyp,
  body: GeschaeftspartnerIn,
): Promise<Geschaeftspartner> {
  return api.post<Geschaeftspartner>(`/kontakte/geschaeftspartner?typ=${typ}`, body);
}

export function updateGeschaeftspartner(
  id: number,
  body: GeschaeftspartnerIn,
): Promise<Geschaeftspartner> {
  return api.put<Geschaeftspartner>(`/kontakte/geschaeftspartner/${id}`, body);
}

export interface KontaktOhneNummer {
  id: number;
  typ: GeschaeftspartnerTyp;
  firmenname: string;
  plz: string | null;
  ort: string | null;
  email: string | null;
}

export interface KontakteOhneNummerResponse {
  total: number;
  items: KontaktOhneNummer[];
  truncated: boolean;
}

export function listKontakteOhneNummer(limit = 500): Promise<KontakteOhneNummerResponse> {
  return api.get<KontakteOhneNummerResponse>(`/kontakte/ohne-nummer?limit=${limit}`);
}

export function countKontakteOhneNummer(): Promise<{ count: number }> {
  return api.get<{ count: number }>("/kontakte/ohne-nummer/count");
}

export function setGeschaeftspartnerNummer(
  id: number,
  nummer: string,
): Promise<Geschaeftspartner> {
  return api.put<Geschaeftspartner>(`/kontakte/geschaeftspartner/${id}/nummer`, { nummer });
}

export function updateKundeRechnungsEinstellungen(
  id: number,
  body: KundeRechnungsEinstellungen,
): Promise<Geschaeftspartner> {
  return api.put<Geschaeftspartner>(
    `/kontakte/geschaeftspartner/${id}/rechnungs-einstellungen`,
    body,
  );
}

export function getStandardZahlungsbedingungen(): Promise<ZahlungsbedingungenStandard> {
  return api.get<ZahlungsbedingungenStandard>("/kontakte/zahlungsbedingungen/standard");
}

export function updateStandardZahlungsbedingungen(
  body: ZahlungsbedingungenStandard,
): Promise<ZahlungsbedingungenStandard> {
  return api.put<ZahlungsbedingungenStandard>("/kontakte/zahlungsbedingungen/standard", body);
}

export function updateKundeZahlungsbedingungen(
  id: number,
  body: ZahlungsbedingungenOverride,
): Promise<Geschaeftspartner> {
  return api.put<Geschaeftspartner>(
    `/kontakte/geschaeftspartner/${id}/zahlungsbedingungen`,
    body,
  );
}

export function listKundeProjektZahlungsbedingungen(
  geschaeftspartnerId: number,
): Promise<ProjektZahlungsbedingungen[]> {
  return api.get<ProjektZahlungsbedingungen[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/projekt-zahlungsbedingungen`,
  );
}

export function updateProjektZahlungsbedingungen(
  auftragId: number,
  body: ZahlungsbedingungenOverride,
): Promise<ProjektZahlungsbedingungen> {
  return api.put<ProjektZahlungsbedingungen>(
    `/kontakte/projekt-zahlungsbedingungen/${auftragId}`,
    body,
  );
}

export function deleteGeschaeftspartner(id: number): Promise<{ message: string }> {
  return api.del<{ message: string }>(`/kontakte/geschaeftspartner/${id}`);
}

export interface LieferantEingangsrechnungArtikelStat {
  bezeichnung: string;
  seller_article_nr?: string | null;
  ean?: string | null;
  einheit?: string | null;
  bestellungen_anzahl: number;
  menge_summe?: number | null;
  erste_rechnung?: string | null;
  letzte_rechnung?: string | null;
  letzter_ep_netto?: number | null;
  stamm_artikel_id?: number | null;
  stamm_hero_nr?: string | null;
  stamm_artikel_bezeichnung?: string | null;
}

export async function getLieferantEingangsrechnungArtikel(
  gpId: number,
  params?: { limit?: number },
): Promise<{ lieferant_geschaeftspartner_id: number; items: LieferantEingangsrechnungArtikelStat[]; total: number }> {
  const qs = params?.limit != null ? `?limit=${params.limit}` : "";
  return api.get(`/kontakte/geschaeftspartner/${gpId}/eingangsrechnung-artikel${qs}`);
}

export function getKontaktAnsprechpartner(id: number): Promise<KontaktAnsprechpartner> {
  return api.get<KontaktAnsprechpartner>(`/kontakte/ansprechpartner/${id}`, { cache: "no-store" });
}

export function createKontaktAnsprechpartner(
  body: KontaktAnsprechpartnerIn,
): Promise<KontaktAnsprechpartner> {
  return api.post<KontaktAnsprechpartner>("/kontakte/ansprechpartner", body);
}

export function updateKontaktAnsprechpartner(
  id: number,
  body: KontaktAnsprechpartnerIn,
): Promise<KontaktAnsprechpartner> {
  return api.put<KontaktAnsprechpartner>(`/kontakte/ansprechpartner/${id}`, body);
}

export function deleteKontaktAnsprechpartner(id: number): Promise<{ message: string }> {
  return api.del<{ message: string }>(`/kontakte/ansprechpartner/${id}`);
}

export function ansprechpartnerDisplayName(ap: KontaktAnsprechpartner): string {
  const parts = [ap.anrede, ap.vorname, ap.nachname].filter(Boolean);
  return parts.join(" ").trim() || "—";
}

export interface GpBestellnummerRegel {
  id: number;
  geschaeftspartner_id: number;
  bestellnummer: string;
  ansprechpartner_id: number | null;
  hero_address_id: number | null;
  ansprechpartner_name: string | null;
  ort_bezeichnung: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GpBestellnummerRegelIn {
  bestellnummer: string;
  ansprechpartner_id?: number | null;
  hero_address_id?: number | null;
}

export function listGpBestellnummerRegeln(geschaeftspartnerId: number): Promise<GpBestellnummerRegel[]> {
  return api.get<GpBestellnummerRegel[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/bestellnummer-regeln`,
  );
}

export function resolveGpBestellnummer(
  geschaeftspartnerId: number,
  params?: { ansprechpartner_id?: number | null; hero_address_id?: number | null },
): Promise<{ bestellnummer: string | null }> {
  const search = new URLSearchParams();
  if (params?.ansprechpartner_id != null) {
    search.set("ansprechpartner_id", String(params.ansprechpartner_id));
  }
  if (params?.hero_address_id != null) {
    search.set("hero_address_id", String(params.hero_address_id));
  }
  const qs = search.toString();
  return api.get<{ bestellnummer: string | null }>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/bestellnummer-regeln/resolve${qs ? `?${qs}` : ""}`,
  );
}

export function createGpBestellnummerRegel(
  geschaeftspartnerId: number,
  body: GpBestellnummerRegelIn,
): Promise<GpBestellnummerRegel> {
  return api.post<GpBestellnummerRegel>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/bestellnummer-regeln`,
    body,
  );
}

export function updateGpBestellnummerRegel(
  geschaeftspartnerId: number,
  regelId: number,
  body: GpBestellnummerRegelIn,
): Promise<GpBestellnummerRegel> {
  return api.put<GpBestellnummerRegel>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/bestellnummer-regeln/${regelId}`,
    body,
  );
}

export function deleteGpBestellnummerRegel(
  geschaeftspartnerId: number,
  regelId: number,
): Promise<{ message: string }> {
  return api.del<{ message: string }>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/bestellnummer-regeln/${regelId}`,
  );
}

export interface GpRechnungsadresse {
  id: number;
  geschaeftspartner_id: number;
  bezeichnung: string | null;
  firma: string | null;
  adresszusatz: string | null;
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  is_default: boolean;
  hero_contact_id?: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GpRechnungsadresseIn {
  bezeichnung?: string | null;
  firma?: string | null;
  adresszusatz?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
  land?: string | null;
  is_default?: boolean;
}

export function listGpRechnungsadressen(
  geschaeftspartnerId: number,
): Promise<GpRechnungsadresse[]> {
  return api.get<GpRechnungsadresse[]>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/rechnungsadressen`,
  );
}

export function createGpRechnungsadresse(
  geschaeftspartnerId: number,
  body: GpRechnungsadresseIn,
): Promise<GpRechnungsadresse> {
  return api.post<GpRechnungsadresse>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/rechnungsadressen`,
    body,
  );
}

export function updateGpRechnungsadresse(
  geschaeftspartnerId: number,
  adresseId: number,
  body: GpRechnungsadresseIn,
): Promise<GpRechnungsadresse> {
  return api.put<GpRechnungsadresse>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/rechnungsadressen/${adresseId}`,
    body,
  );
}

export function deleteGpRechnungsadresse(
  geschaeftspartnerId: number,
  adresseId: number,
): Promise<{ message: string }> {
  return api.del<{ message: string }>(
    `/kontakte/geschaeftspartner/${geschaeftspartnerId}/rechnungsadressen/${adresseId}`,
  );
}

export function formatGpRechnungsadresse(addr: Pick<
  GpRechnungsadresse,
  "bezeichnung" | "firma" | "adresszusatz" | "strasse" | "hausnummer" | "plz" | "ort"
>): string {
  const street = [addr.strasse, addr.hausnummer].filter(Boolean).join(" ").trim();
  const city = [addr.plz, addr.ort].filter(Boolean).join(" ").trim();
  const parts = [addr.bezeichnung, addr.firma, addr.adresszusatz, street, city].filter(Boolean);
  return parts.join(" · ") || "—";
}

/** Excel-Export der Objektadressen eines Kunden (für KI-/Vorlagen-Ausfüllen). */
export function kundeOrteExportDownloadUrl(geschaeftspartnerId: number): string {
  return `${API_BASE}/kontakte/kunden/${geschaeftspartnerId}/orte/export.xlsx`;
}
